export type {components as OpenApiComponents, operations as OpenApiOperations, paths as OpenApiPaths} from "./generated";

export type AccountRole = "user" | "admin";

const SHANGHAI_TIME_ZONE = "Asia/Shanghai";
const SHANGHAI_DATE_FORMAT = new Intl.DateTimeFormat("en-CA", {
  timeZone: SHANGHAI_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const SHANGHAI_DATE_TIME_FORMAT = new Intl.DateTimeFormat("en-CA", {
  timeZone: SHANGHAI_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

function dateParts(formatter: Intl.DateTimeFormat, value: Date): Record<string, string> {
  return Object.fromEntries(
    formatter.formatToParts(value).filter((item) => item.type !== "literal").map((item) => [item.type, item.value]),
  );
}

export function shanghaiDate(value: Date | string | number = new Date()): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return typeof value === "string" ? value : "";
  const parts = dateParts(SHANGHAI_DATE_FORMAT, date);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

export function shanghaiDateTime(value: Date | string | number): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return typeof value === "string" ? value : "";
  const parts = dateParts(SHANGHAI_DATE_TIME_FORMAT, date);
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
}

export interface SessionUser {
  email: string;
  name: string;
  role: AccountRole;
}

export interface SessionState {
  ok: boolean;
  authenticated: boolean;
  user: SessionUser;
  csrfToken: string;
}

export interface SystemEntry {
  id: "reimbursement" | "attendance" | "cloud-drive" | "settings";
  title: string;
  description: string;
  category: string;
  route: string;
  icon: string;
  accountRoles: AccountRole[];
  actingRoles: AccountRole[];
  enabled: boolean;
  order: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(status: number, detail: unknown) {
    const message = Array.isArray(detail)
      ? detail.map((item) => (typeof item === "string" ? item : JSON.stringify(item))).join("\n")
      : typeof detail === "string"
        ? detail
        : "请求失败，请稍后重试。";
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

const ROLE_KEY = "nlrm-role-view";
let csrfToken = "";

export function setCsrfToken(value: string): void {
  csrfToken = value || "";
}

export function getCsrfToken(): string {
  return csrfToken;
}

export function actingRole(user?: SessionUser | null): AccountRole {
  if (!user || user.role !== "admin") return "user";
  return localStorage.getItem(ROLE_KEY) === "user" ? "user" : "admin";
}

export function setActingRole(role: AccountRole): void {
  localStorage.setItem(ROLE_KEY, role);
}

function detailFromPayload(payload: unknown): unknown {
  if (!payload || typeof payload !== "object") return payload;
  const value = payload as Record<string, unknown>;
  return value.detail ?? value.error ?? payload;
}

export async function api<T>(
  path: string,
  options: RequestInit = {},
  user?: SessionUser | null,
): Promise<T> {
  const headers = new Headers(options.headers || {});
  if (!(options.body instanceof FormData) && options.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("X-Acting-Role", actingRole(user));
  const method = (options.method || "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) {
    headers.set("X-CSRF-Token", csrfToken);
  }
  const response = await fetch(path, {...options, headers, credentials: "include"});
  const contentType = response.headers.get("Content-Type") || "";
  const payload = contentType.includes("application/json") ? await response.json() : await response.text();
  if (!response.ok) throw new ApiError(response.status, detailFromPayload(payload));
  return payload as T;
}

export async function download(
  path: string,
  options: RequestInit = {},
  user?: SessionUser | null,
): Promise<{blob: Blob; fileName: string}> {
  const headers = new Headers(options.headers || {});
  headers.set("X-Acting-Role", actingRole(user));
  const method = (options.method || "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken) headers.set("X-CSRF-Token", csrfToken);
  if (!(options.body instanceof FormData) && options.body !== undefined) headers.set("Content-Type", "application/json");
  const response = await fetch(path, {...options, headers, credentials: "include"});
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new ApiError(response.status, detailFromPayload(payload));
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  return {blob: await response.blob(), fileName: encoded ? decodeURIComponent(encoded) : plain || "download"};
}

export function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function loadSession(): Promise<SessionState> {
  const result = await api<SessionState>("/api/v1/auth/me");
  setCsrfToken(result.csrfToken);
  return result;
}
